import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReviewApi, UploadApi } from '../api';
import { useAuth } from '../context/AuthContext';

const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const MAX_MEDIA = 6;
const MAX_BYTES = 20 * 1024 * 1024;

function Stars({ value = 0, size = 16, onPick }) {
  return (
    <span className={`stars ${onPick ? 'pick' : ''}`} style={{ fontSize: size }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          className={`${n <= value ? 'fas' : 'far'} fa-star`}
          onClick={onPick ? () => onPick(n) : undefined}
          role={onPick ? 'button' : undefined}
          aria-label={onPick ? `${n} star${n > 1 ? 's' : ''}` : undefined}
        />
      ))}
    </span>
  );
}

// Rating summary + review list, with a write form for verified buyers.
export default function Reviews({ productId }) {
  const { isLoggedIn } = useAuth();
  const [data, setData] = useState(null);
  const [can, setCan] = useState({ canReview: false, review: null });

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    ReviewApi.forProduct(productId).then(setData).catch(() => setData({ items: [], summary: null }));
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isLoggedIn) { setCan({ canReview: false, review: null }); return; }
    ReviewApi.can(productId)
      .then((d) => {
        setCan(d);
        if (d.review) {
          setRating(d.review.rating);
          setComment(d.review.comment || '');
          setMedia(d.review.media || []);
        }
      })
      .catch(() => {});
  }, [isLoggedIn, productId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (media.length >= MAX_MEDIA) { setMsg(`You can attach up to ${MAX_MEDIA} files.`); return; }
    if (file.size > MAX_BYTES) { setMsg('File must be under 20 MB.'); return; }

    setUploading(true);
    setMsg('');
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const up = await UploadApi.file(reader.result, 'reviews');
        setMedia((m) => [...m, { type: up.type, url: up.url }]);
      } catch (err) { setMsg(err.message); } finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!rating) { setMsg('Please pick a star rating.'); return; }
    setBusy(true); setMsg('');
    try {
      await ReviewApi.save({ productId, rating, comment, media });
      setOpen(false);
      load();
      const d = await ReviewApi.can(productId).catch(() => null);
      if (d) setCan(d);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  if (!data) return null;

  const { summary, items = [] } = data;
  const total = summary?.total || 0;

  return (
    <section className="grey-bg" id="reviews">
      <div className="container">
        <div className="section-head"><h2>Ratings &amp; Reviews</h2></div>

        <div className="review-layout">
          {/* Summary */}
          <aside className="review-summary">
            <div className="rs-score">{summary?.average ? summary.average.toFixed(1) : '—'}</div>
            <Stars value={Math.round(summary?.average || 0)} size={18} />
            <p className="muted small">{total} review{total === 1 ? '' : 's'}</p>

            <div className="rs-bars">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = summary?.breakdown?.[n] || 0;
                const pct = total ? (c / total) * 100 : 0;
                return (
                  <div className="rs-bar" key={n}>
                    <span>{n}★</span>
                    <div className="rs-track"><div className="rs-fill" style={{ width: `${pct}%` }} /></div>
                    <span className="muted">{c}</span>
                  </div>
                );
              })}
            </div>

            {/* Only delivered buyers can review — the server enforces it too. */}
            {!isLoggedIn ? (
              <p className="muted small">
                <Link className="link-btn inline" to="/login">Login</Link> to review this product.
              </p>
            ) : can.canReview ? (
              <button className="btn-gold" onClick={() => setOpen((o) => !o)}>
                {can.review ? 'Edit your review' : 'Write a review'}
              </button>
            ) : (
              <p className="muted small">
                <i className="fas fa-lock" /> You can review this once your order is delivered.
              </p>
            )}
          </aside>

          {/* Write + list */}
          <div>
            {open && can.canReview ? (
              <div className="panel-card review-form">
                <h3>{can.review ? 'Edit your review' : 'Write a review'}</h3>

                <div className="field">
                  <label>Your rating *</label>
                  <Stars value={rating} size={26} onPick={setRating} />
                </div>

                <div className="field">
                  <label>Your review</label>
                  <textarea
                    rows={4}
                    maxLength={1500}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="What did you like or dislike about it?"
                  />
                </div>

                <div className="field">
                  <label>Photos or video (optional)</label>
                  <div className="rv-media">
                    {media.map((m, i) => (
                      <div className="rv-thumb" key={m.url}>
                        {m.type === 'video'
                          ? <video src={m.url} muted />
                          : <img src={m.url} alt="" />}
                        <button onClick={() => setMedia((x) => x.filter((_, idx) => idx !== i))}>✕</button>
                      </div>
                    ))}
                    {uploading ? <div className="rv-thumb loading">…</div> : null}
                    {media.length < MAX_MEDIA && !uploading ? (
                      <label className="rv-add">
                        ＋
                        <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={onFile} hidden />
                      </label>
                    ) : null}
                  </div>
                </div>

                {msg ? <p className="form-msg err">{msg}</p> : null}
                <button className="consult-btn" style={{ justifyContent: 'center' }} disabled={busy || uploading} onClick={submit}>
                  {busy ? 'Posting…' : can.review ? 'Update Review' : 'Post Review'}
                </button>
              </div>
            ) : null}

            {items.length === 0 ? (
              <div className="panel-card"><p className="muted">No reviews yet — be the first to review this product.</p></div>
            ) : (
              items.map((r) => (
                <div className="review-card" key={r._id}>
                  <div className="rv-head">
                    <div className="rv-avatar">{(r.name || 'C')[0].toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <strong>{r.name}</strong>
                      <span className="rv-verified"><i className="fas fa-circle-check" /> Verified Buyer</span>
                    </div>
                    <span className="muted small">{fmt(r.at)}</span>
                  </div>

                  <Stars value={r.rating} />
                  {r.comment ? <p className="rv-comment">{r.comment}</p> : null}

                  {r.media?.length ? (
                    <div className="rv-media">
                      {r.media.map((m) => (
                        <a className="rv-thumb" key={m.url} href={m.url} target="_blank" rel="noreferrer">
                          {m.type === 'video' ? <video src={m.url} muted /> : <img src={m.url} alt="" />}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
